import { Component, type ErrorInfo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import { ErrorState } from '@eis/patterns';

interface Props {
  /** Module id — so a boundary reset is scoped to the module that failed. */
  readonly moduleId: string;
  readonly title: string;
  readonly description: string;
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

/**
 * Module-scoped error boundary (backbone §3 "isolation mechanics").
 *
 * A crash or bad deploy in one module renders a contained error state; the shell chrome and every
 * other module keep working. That containment is the point of the modular monolith — without it,
 * one team's exception is everyone's outage.
 */
export class ModuleErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Telemetry hook: this is where Sentry/Datadog receives the module-scoped context.
    console.error(`[module:${this.props.moduleId}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <Box sx={{ p: 3 }}>
        <ErrorState
          title={this.props.title}
          description={this.props.description}
          details={error.stack ?? error.message}
          onRetry={() => {
            this.setState({ error: null });
          }}
        />
      </Box>
    );
  }
}
